import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { HelpCircle, Loader2 } from 'lucide-react';
import PublicHeader from '../../components/self-service/PublicHeader';
import SignInModal from '../../components/self-service/auth/SignInModal';
import { useAuth } from '../../components/self-service/auth/useAuth';
import { useStationHelpBranding } from '../../components/self-service/help/helpStationBranding';
import PrimaryButton from '../../components/self-service/shared/PrimaryButton';
import { accentVars, tintFromAccent } from '../../components/self-service/steps/_shared';
import { listMyTickets, type SupportTicketSummary } from '../../services/helpTicketService';
import { useHelpCompletedNotification } from '../../components/self-service/help/useHelpNotifications';
import TicketThreadPanel from '../../components/self-service/help/TicketThreadPanel';
import { formatDateTime } from '../../components/self-service/shared/dateFormat';

function statusPill(status: SupportTicketSummary['status']) {
  switch (status) {
    case 'open':
      return 'bg-yellow-100 text-yellow-800';
    case 'in_progress':
      return 'bg-blue-100 text-blue-800';
    case 'completed':
      return 'bg-green-100 text-green-800';
  }
}

const HelpCenterPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { user, isAuthenticated } = useAuth();
  const { config, loading: configLoading, accent, primary } = useStationHelpBranding(slug);
  const { markCompletedSeen } = useHelpCompletedNotification(user?.id);

  const [signInOpen, setSignInOpen] = useState(false);
  const [tickets, setTickets] = useState<SupportTicketSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    markCompletedSeen();
  }, [markCompletedSeen]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const rows = await listMyTickets();
        setTickets(rows);
        if (rows.length > 0) {
          setSelectedId((prev) => prev ?? rows[0].id);
        }
      } catch {
        setError('Could not load your tickets.');
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [isAuthenticated]);

  if (configLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
      </div>
    );
  }

  if (!config) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-600">
        Station not found.
      </div>
    );
  }

  const pageStyle = {
    fontFamily: config.fontFamily,
    ...(accent ? accentVars(accent, primary) : {}),
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-white flex flex-col" style={pageStyle}>
        <PublicHeader publisher={config} />
        <main className="max-w-lg mx-auto px-4 py-16 text-center">
          <HelpCircle
            className="w-10 h-10 mx-auto mb-4"
            style={accent ? { color: accent } : { color: '#94a3b8' }}
          />
          <h1 className="text-xl font-semibold text-slate-900 mb-2">Help Center</h1>
          <p className="text-slate-600 mb-6">Sign in to view your support tickets.</p>
          <PrimaryButton type="button" accentColor={accent} primaryColor={primary} onClick={() => setSignInOpen(true)}>
            Sign in
          </PrimaryButton>
          <SignInModal open={signInOpen} onClose={() => setSignInOpen(false)} />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col" style={pageStyle}>
      <PublicHeader publisher={config} />
      <main className="flex-1 max-w-5xl mx-auto w-full px-4 md:px-6 py-8">
        <h1 className="text-2xl font-semibold text-slate-900 mb-1">Help Center</h1>
        <p className="text-sm text-slate-600 mb-6">Your support requests across all stations.</p>

        {loading ? (
          <div className="text-slate-500 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading tickets…
          </div>
        ) : error ? (
          <p className="text-red-600 text-sm">{error}</p>
        ) : tickets.length === 0 ? (
          <p className="text-slate-600 text-sm">
            No tickets yet. Use the help button in the campaign wizard to ask our support team.
          </p>
        ) : (
          <div className="grid md:grid-cols-[minmax(0,280px)_1fr] gap-6">
            <ul
              className="space-y-2 border rounded-xl p-2"
              style={
                accent
                  ? { borderColor: tintFromAccent(accent, 0.2), backgroundColor: tintFromAccent(accent, 0.04) }
                  : { borderColor: '#e2e8f0', backgroundColor: '#f8fafc' }
              }
            >
              {tickets.map((t) => (
                <li key={t.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(t.id)}
                    className={`w-full text-left rounded-lg px-3 py-2.5 text-sm transition-colors ${
                      selectedId === t.id
                        ? 'bg-white shadow-sm border'
                        : 'hover:bg-white/80'
                    }`}
                    style={
                      selectedId === t.id && accent
                        ? { borderColor: tintFromAccent(accent, 0.35) }
                        : selectedId === t.id
                          ? { borderColor: '#e2e8f0' }
                          : undefined
                    }
                  >
                    <div className="font-medium text-slate-900 truncate">{t.categoryLabel}</div>
                    <div className="text-xs text-slate-500 truncate">{t.stationName ?? t.stationSlug}</div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${statusPill(t.status)}`}>
                        {t.statusLabel}
                      </span>
                      <span className="text-[10px] text-slate-400">{formatDateTime(t.createdAt)}</span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
            <div className="border border-slate-200 rounded-xl p-4 min-h-[280px]">
              {selectedId ? (
                <TicketThreadPanel ticketId={selectedId} accentColor={accent} primaryColor={primary} />
              ) : (
                <p className="text-sm text-slate-500">Select a ticket.</p>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default HelpCenterPage;
